import type { LoaderFunction, MetaFunction } from "@remix-run/node";
import {
  json,
  Link,
  MetaDescriptor,
  useLoaderData,
  useNavigate,
} from "@remix-run/react";
import {
  IconBrandGithub,
  IconBrandNpm,
  IconBug,
  IconChevronDown,
  IconChevronUp,
  IconEye,
  IconGitFork,
  IconStar,
  IconThumbDown,
  IconThumbUp,
} from "@tabler/icons-react";
import "github-markdown-css/github-markdown-light.css";
import "prismjs/themes/prism.css";
import { useState } from "react";
import { FeedbackPopover } from "~/components/FeedbackPopover";
import { NpmDownloadsChart } from "~/components/NpmDownloadsChart";
import { capitalizeFirstLetter } from "~/components/PageHeader";
import { ProjectCard } from "~/components/ProjectCard";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "~/components/ui/carousel";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import { longCacheControlHeaderValue } from "~/utils/cacheControl";
import {
  getCodeExample,
  getCompetitorComparison,
  getValidCompetitors,
  getNpmPackageName,
} from "~/utils/claude";
import { fetchGitHubRepo, fetchGitHubRepoReadme } from "~/utils/github";
import { processMarkdown } from "~/utils/markdown";

export const meta: MetaFunction = ({ params, data }) => {
  if (!data) return [];

  const { stargazers_count } = data as LoaderData;
  const formattedName = capitalizeFirstLetter(params.name || "");

  const year = new Date().getFullYear();
  const metaTags: MetaDescriptor[] = [
    {
      title: `${formattedName} Overview, Examples, Pros and Cons in ${year}`,
    },
    {
      name: "description",
      content: `Compare ${formattedName} with alternative projects. View features, pros, cons, and usage examples.`,
    },
  ];

  // Add noindex, nofollow if the project has less than 300 stars
  if (typeof stargazers_count === "number" && stargazers_count < 300) {
    metaTags.push({ name: "robots", content: "noindex, nofollow" });
  }

  return metaTags;
};

type GitHubRepoData = Awaited<ReturnType<typeof fetchGitHubRepo>>;
type NpmDownloadData = { date: string; downloads: number }[];

type LoaderData = GitHubRepoData & {
  readmeHtml: string;
  npmPackage: string | null;
  npmDownloads: NpmDownloadData | null;
  competitors: any[];
  codeExampleHtml: string;
  competitorComparisons: { repo: any; comparison: string }[];
};

export async function fetchNpmDownloads(
  packageName: string
): Promise<NpmDownloadData> {
  const response = await fetch(
    `https://api.npmjs.org/downloads/range/last-month/${packageName}`
  );
  if (!response.ok) {
    throw new Error("Failed to fetch NPM download data");
  }
  const data = await response.json();
  return data.downloads.map((item: any) => ({
    date: new Date(item.day).toLocaleDateString(),
    downloads: item.downloads,
  }));
}

export const loader: LoaderFunction = async ({ params, request }) => {
  const throwParam = new URL(request.url).searchParams.has("throw");
  if (throwParam) {
    throw new Error("Test error");
  }
  const repoData = await fetchGitHubRepo(params.owner!, params.name!);
  if (!repoData) {
    throw new Response("No repo data found", { status: 404 });
  }

  if (repoData.stargazers_count < 1000) {
    throw new Response("Not Found", { status: 404 });
  }

  const [readmeContent, competitors, codeExampleContent, npmPackage] =
    await Promise.all([
      fetchGitHubRepoReadme(params.owner!, params.name!).catch(() => null),
      getValidCompetitors(`${params.owner}/${params.name}`).catch(() => []),
      getCodeExample(`${params.owner}/${params.name}`).catch(() => ""),
      getNpmPackageName(`${params.owner}/${params.name}`).catch(() => null),
    ]);

  const [readmeHtml, codeExampleHtml, competitorComparisons] =
    await Promise.all([
      processMarkdown(readmeContent || "", `${params.owner}/${params.name}`),
      processMarkdown(codeExampleContent || ""),
      Promise.all(
        competitors
          .filter((competitor: any) => competitor.stargazers_count >= 1000)
          .map(async (competitor: any) => ({
            repo: competitor,
            comparison: await processMarkdown(
              await getCompetitorComparison(
                `${params.owner}/${params.name}`,
                `${competitor.owner.login}/${competitor.name}`
              )
            ),
          }))
      ),
    ]);

  let npmDownloads = null;
  if (npmPackage) {
    try {
      npmDownloads = await fetchNpmDownloads(npmPackage);
    } catch (error) {
      console.error("Error fetching NPM download data:", error);
    }
  }

  if (!repoData || !readmeHtml) {
    throw new Response("Failed to get github response", { status: 500 });
  }

  if (repoData.stargazers_count < 1000) {
    throw new Response("Not Found", { status: 404 });
  }

  return json(
    {
      ...repoData,
      readmeHtml,
      npmPackage,
      npmDownloads,
      competitors,
      codeExampleHtml,
      competitorComparisons,
    },
    { headers: { "Cache-Control": longCacheControlHeaderValue } }
  );
};

export default function LibraryDetails() {
  const {
    readmeHtml,
    npmPackage,
    npmDownloads,
    competitors,
    codeExampleHtml,
    competitorComparisons,
    ...repoData
  } = useLoaderData<LoaderData>();

  const [allCardsExpanded, setAllCardsExpanded] = useState(false);
  const navigate = useNavigate();

  const toggleAllCards = () => {
    setAllCardsExpanded(!allCardsExpanded);
  };

  return (
    <div className="container mx-auto px-4 md:px-12 py-8">
      <div className="flex flex-col md:flex-row justify-between items-start mb-8 mt-4">
        <div className="flex items-center">
          <div>
            <h1 className="text-4xl font-bold mb-4 text-foreground">
              <img
                src={repoData.owner.avatar_url}
                alt={`${repoData.owner.login} logo`}
                className="w-12 h-12 rounded-full mr-4 inline-block"
              />
              <a
                href={repoData.html_url}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
              >
                {repoData.name || "Library Details"}
              </a>
            </h1>
            <p className="text-xl text-muted-foreground mb-4">
              {repoData.description || "No description available"}
            </p>
            {repoData.stargazers_count !== undefined && (
              <div className="flex items-center space-x-6 mb-4">
                <div className="flex items-center">
                  <IconStar className="mr-2" size={20} />
                  <span className="text-2xl font-bold">
                    {repoData.stargazers_count?.toLocaleString() ?? "N/A"}
                  </span>
                </div>
                <div className="flex items-center text-sm text-muted-foreground">
                  <IconGitFork className="mr-1" size={16} />
                  <span>{repoData.forks_count?.toLocaleString() ?? "N/A"}</span>
                </div>
                <div className="flex items-center text-sm text-muted-foreground">
                  <IconEye className="mr-1" size={16} />
                  <span>
                    {repoData.watchers_count?.toLocaleString() ?? "N/A"}
                  </span>
                </div>
                <div className="flex items-center text-sm text-muted-foreground max-md:hidden">
                  <IconBug className="mr-1" size={16} />
                  <span>
                    {repoData.open_issues_count?.toLocaleString() ?? "N/A"}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex space-x-4">
          {repoData.html_url && (
            <Button asChild variant="outline">
              <a
                href={repoData.html_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center"
              >
                <IconBrandGithub className="mr-2" size={20} />
                View on GitHub
              </a>
            </Button>
          )}
          {npmPackage && (
            <Button asChild variant="outline">
              <a
                href={`https://www.npmjs.com/package/${npmPackage}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center"
              >
                <IconBrandNpm className="mr-2" size={20} />
                View on NPM
              </a>
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        <div className="lg:w-2/3 flex flex-col gap-8">
          {competitors?.length > 0 && (
            <div className="lg:hidden">
              <h2 className="text-2xl font-semibold">Top Related Projects</h2>
              <div className="overflow-x-auto py-4 -mx-4 px-4">
                <div className="flex space-x-4">
                  {competitors.map((competitor: any) => (
                    <div
                      className="w-64 flex-shrink-0"
                      key={`${competitor.owner}/${competitor.name}`}
                    >
                      <ProjectCard
                        name={competitor.name}
                        description={competitor.description}
                        stargazersCount={competitor.stargazers_count}
                        ownerLogin={competitor.owner.login}
                        ownerAvatarUrl={competitor.owner.avatar_url}
                        openGraphImageUrl={competitor.open_graph_image_url}
                        baseProjectName={repoData.name}
                        baseProjectOwner={repoData.owner.login}
                        variant="small"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {codeExampleHtml && (
            <div>
              <h2 className="text-2xl font-semibold mb-4">Quick Overview</h2>
              <Card className="flex flex-col">
                <CardContent className="mt-6 flex-grow flex flex-col overflow-hidden">
                  <div
                    className={`prose markdown-body dark:prose-invert !bg-transparent dark:text-white max-w-none flex-grow overflow-y-auto`}
                    dangerouslySetInnerHTML={{ __html: codeExampleHtml }}
                  />
                </CardContent>
              </Card>
              <div className="flex justify-end mt-2 space-x-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="sm" className="p-1">
                        <FeedbackPopover
                          placement="quickstart"
                          sentiment="positive"
                          metadata={`${repoData.owner.login}/${repoData.name}\n\n${codeExampleHtml}`}
                        >
                          <IconThumbUp size={20} />
                        </FeedbackPopover>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>This overview is helpful</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="sm" className="p-1">
                        <FeedbackPopover
                          placement="quickstart"
                          sentiment="negative"
                          metadata={`${repoData.owner.login}/${repoData.name}\n\n${codeExampleHtml}`}
                        >
                          <IconThumbDown size={20} />
                        </FeedbackPopover>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>This overview is not helpful</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          )}

          {competitorComparisons.length > 0 && (
            <div>
              <h2 className="text-2xl font-semibold mb-4">
                Competitor Comparisons
              </h2>
              <Carousel className="w-full lg:w-11/12">
                <CarouselContent>
                  {competitorComparisons.map((comparison: any) => (
                    <CarouselItem
                      key={comparison.repo.name}
                      className="basis-5/6 snap-center"
                    >
                      <div className="p-1">
                        <Card className="flex flex-col">
                          <CardHeader className="border-b-1 border-b border-neutral-200 dark:border-neutral-800">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <img
                                  src={comparison.repo.owner.avatar_url}
                                  alt={`${comparison.repo.owner.login} logo`}
                                  className="w-8 h-8 rounded-full"
                                />
                                <CardTitle>
                                  <Link
                                    to={`/library/${comparison.repo.owner.login}/${comparison.repo.name}`}
                                    className="hover:underline"
                                  >
                                    {comparison.repo.name}
                                  </Link>
                                </CardTitle>
                              </div>
                              <div className="flex items-center">
                                <IconStar className="mr-1" size={16} />
                                <span>
                                  {comparison.repo.stargazers_count.toLocaleString()}
                                </span>
                              </div>
                            </div>
                            <CardDescription>
                              {comparison.repo.description}
                            </CardDescription>
                          </CardHeader>
                          <CardContent className="mt-6 flex-grow flex flex-col overflow-hidden">
                            <div
                              className={`prose markdown-body dark:prose-invert !bg-transparent dark:text-white max-w-none flex-grow overflow-hidden ${
                                allCardsExpanded ? "" : "max-h-[350px]"
                              }`}
                              dangerouslySetInnerHTML={{
                                __html: comparison.comparison,
                              }}
                            />
                            <div className="mt-4 flex justify-between items-center">
                              <Button
                                variant="outline"
                                size="sm"
                                className="px-4 py-2 text-sm"
                                onClick={(e) => {
                                  e.stopPropagation();

                                  const url = `/compare/${repoData.owner.login}>${repoData.name}/${comparison.repo.owner.login}>${comparison.repo.name}`;
                                  if (e.shiftKey || e.metaKey) {
                                    window.open(url, "_blank");
                                  } else {
                                    navigate(url);
                                  }
                                }}
                              >
                                Detailed Comparison
                              </Button>
                              <Button
                                variant="link"
                                onClick={toggleAllCards}
                                className="text-sm"
                              >
                                {allCardsExpanded ? (
                                  <>
                                    Collapse{" "}
                                    <IconChevronUp size={16} className="ml-1" />
                                  </>
                                ) : (
                                  <>
                                    Show more{" "}
                                    <IconChevronDown
                                      size={16}
                                      className="ml-1"
                                    />
                                  </>
                                )}
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                        <div className="flex justify-end mt-2 space-x-2">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="p-1"
                                >
                                  <FeedbackPopover
                                    placement="competitor-comparison"
                                    sentiment="positive"
                                    metadata={`${repoData.owner.login}/${repoData.name}-vs-${comparison.repo.owner.login}/${comparison.repo.name}\n\n${comparison.comparison}`}
                                  >
                                    <IconThumbUp size={20} />
                                  </FeedbackPopover>
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>This comparison is helpful</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="p-1"
                                >
                                  <FeedbackPopover
                                    placement="competitor-comparison"
                                    sentiment="negative"
                                    metadata={`${repoData.owner.login}/${repoData.name}-vs-${comparison.repo.owner.login}/${comparison.repo.name}\n\n${comparison.comparison}`}
                                  >
                                    <IconThumbDown size={20} />
                                  </FeedbackPopover>
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>This comparison is not helpful</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious />
                <CarouselNext />
              </Carousel>
            </div>
          )}

          {/* Add VisualCopilotAd for mobile visitors */}
          <div className="lg:hidden">
            <VisualCopilotAd className="mt-0" />
          </div>

          {readmeHtml && (
            <div>
              <h2 className="text-2xl font-semibold mb-4">README</h2>
              <Card className="p-6">
                <div
                  className="markdown-body !bg-transparent dark:text-white max-w-none"
                  dangerouslySetInnerHTML={{ __html: readmeHtml }}
                />
              </Card>
            </div>
          )}
        </div>
        <div className="lg:w-1/3">
          <div className="flex flex-col gap-y-4">
            {competitors?.length > 0 && (
              <div className="hidden lg:block">
                <h2 className="text-xl font-semibold mb-4">
                  Top Related Projects
                </h2>
                <div className="space-y-4">
                  {competitors.map((competitor: any) => (
                    <ProjectCard
                      key={`${competitor.owner.login}/${competitor.name}`}
                      name={competitor.name}
                      description={competitor.description}
                      stargazersCount={competitor.stargazers_count}
                      ownerLogin={competitor.owner.login}
                      ownerAvatarUrl={competitor.owner.avatar_url}
                      openGraphImageUrl={competitor.open_graph_image_url}
                      baseProjectName={repoData.name}
                      baseProjectOwner={repoData.owner.login}
                    />
                  ))}
                </div>
              </div>
            )}
            {/* Add VisualCopilotAd for desktop visitors */}
            <div className="hidden lg:block">
              <VisualCopilotAd />
            </div>
            {npmPackage && npmDownloads && (
              <div>
                <h2 className="text-xl font-semibold mb-4 mt-8">
                  NPM Downloads
                  <span className="text-muted-foreground text-sm ml-2">
                    Last 30 Days
                  </span>
                </h2>
                <Card className="p-6">
                  <NpmDownloadsChart data={npmDownloads} />
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Remove the breadcrumb handle
export const handle = {};

export function VisualCopilotAd(props: { className?: string }) {
  return (
    <div className={cn("mt-8", props.className)}>
      <h2 className="text-xl font-semibold mb-3">
        Convert{" "}
        <img
          src="https://cdn.builder.io/api/v1/image/assets%2FYJIGb4i01jvw0SRdL5Bt%2Fe730c2a3fc4c4be196631093b3434624"
          alt="Figma logo"
          className="w-5 h-5 max-md:w-4 max-md:h-4 object-contain inline-block"
        />{" "}
        designs to code with AI
      </h2>
      <Card>
        <CardContent className="flex flex-col items-center pt-6">
          <img
            srcSet="https://cdn.builder.io/api/v1/image/assets%2FYJIGb4i01jvw0SRdL5Bt%2F5664ab54712d447c90bc61d00f078d24?format=webp&width=100 100w, https://cdn.builder.io/api/v1/image/assets%2FYJIGb4i01jvw0SRdL5Bt%2F5664ab54712d447c90bc61d00f078d24?format=webp&width=200 200w, https://cdn.builder.io/api/v1/image/assets%2FYJIGb4i01jvw0SRdL5Bt%2F5664ab54712d447c90bc61d00f078d24?format=webp&width=400 400w, https://cdn.builder.io/api/v1/image/assets%2FYJIGb4i01jvw0SRdL5Bt%2F5664ab54712d447c90bc61d00f078d24?format=webp&width=800 800w, https://cdn.builder.io/api/v1/image/assets%2FYJIGb4i01jvw0SRdL5Bt%2F5664ab54712d447c90bc61d00f078d24?format=webp&width=1200 1200w, https://cdn.builder.io/api/v1/image/assets%2FYJIGb4i01jvw0SRdL5Bt%2F5664ab54712d447c90bc61d00f078d24?format=webp&width=1600 1600w, https://cdn.builder.io/api/v1/image/assets%2FYJIGb4i01jvw0SRdL5Bt%2F5664ab54712d447c90bc61d00f078d24?format=webp&width=2000 2000w, https://cdn.builder.io/api/v1/image/assets%2FYJIGb4i01jvw0SRdL5Bt%2F5664ab54712d447c90bc61d00f078d24"
            src="https://cdn.builder.io/api/v1/image/assets%2FYJIGb4i01jvw0SRdL5Bt%2F5664ab54712d447c90bc61d00f078d24"
            alt="Visual Copilot"
            className="w-full aspect-[12/9] object-contain mb-4"
          />
          <CardDescription className="mt-1 mb-3">
            Introducing Visual Copilot: A new AI model to turn Figma designs to
            high quality code using your components.
          </CardDescription>
          <Button className="w-full" asChild>
            <a
              href="https://www.builder.io/m/design-to-code"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                window.gtag("event", "ad_click", {
                  placement: "library-sidebar",
                });
              }}
            >
              Try Visual Copilot
            </a>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
